import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import config from '@/config';
import { handleQuotaError } from '@/utils/quotaErrorHandler';
import { handleAssistantQuotaError } from '@/utils/assistantQuotaErrorHandler';
import { QuotaErrorResponse } from '@/types/agents';

class ApiService {
  private api: AxiosInstance;
  private getAccessTokenFn: (() => Promise<string | null>) | null = null;
  private isRefreshing = false;
  private refreshSubscribers: Array<(token: string) => void> = [];

  constructor() {
    this.api = axios.create({
      baseURL: config.api.baseURL,
      timeout: config.api.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  /**
   * Define a função para recuperar o token de acesso
   */
  public setAccessTokenGetter(fn: () => Promise<string | null>) {
    this.getAccessTokenFn = fn;
  }

  private onTokenRefreshed(token: string) {
    this.refreshSubscribers.forEach((cb) => cb(token));
    this.refreshSubscribers = [];
  }

  private subscribeTokenRefresh(cb: (token: string) => void) {
    this.refreshSubscribers.push(cb);
  }

  /**
   * Configura interceptors de requisição e resposta
   */
  private setupInterceptors() {
    // Interceptor de requisição
    this.api.interceptors.request.use(
      async (config) => {
        // Adiciona token de autenticação
        if (this.getAccessTokenFn) {
          const token = await this.getAccessTokenFn();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }

        // Adiciona token CSRF para Django
        const csrfToken = this.getCsrfToken();
        if (csrfToken) {
          config.headers['X-CSRFToken'] = csrfToken;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Interceptor de resposta
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response) {
          // P14: 402 quota_exceeded → dispatch pra QuotaErrorHost antes de
          // rejeitar. Caller faz `.catch()` pra parar o fluxo; o modal aparece
          // sozinho via custom event.
          if (
            error.response.status === 402 &&
            (error.response.data as { error?: string } | undefined)?.error === 'quota_exceeded'
          ) {
            // G10: cota do Assistente tem modal/endpoints próprios (`/assistant/...`).
            // Roteia 402 de rotas do assistente pro handler dele; o resto vai pro Hub.
            const reqUrl = originalRequest?.url || '';
            if (reqUrl.includes('/assistant/')) {
              handleAssistantQuotaError(error.response.data as QuotaErrorResponse);
            } else {
              handleQuotaError(error.response.data as QuotaErrorResponse);
            }
            return Promise.reject(error);
          }
          // Gate do Hub de Agentes (default OFF pra LEARNER em 2026-06). O
          // AgentRoute já cobre navegação direta; este handler cobre race
          // condition de revogação durante a sessão (admin desliga flag no
          // meio do uso). Toast amigável, sem modal (≠ 402 quota).
          if (
            error.response.status === 403 &&
            (error.response.data as { error?: string } | undefined)?.error === 'agent_feature_not_enabled'
          ) {
            window.dispatchEvent(
              new CustomEvent('agent-feature-blocked', {
                detail: error.response.data,
              }),
            );
            return Promise.reject(error);
          }
          // Gate do Balanceamento de Linha (frente SEOP). Mesmo desenho do bloco
          // acima: o SeopRoute cobre navegação, este handler cobre a revogação da
          // flag no meio da sessão.
          if (
            error.response.status === 403 &&
            (error.response.data as { error?: string } | undefined)?.error === 'seop_feature_not_enabled'
          ) {
            window.dispatchEvent(
              new CustomEvent('seop-feature-blocked', {
                detail: error.response.data,
              }),
            );
            return Promise.reject(error);
          }
          // Gate dos painéis executivos (/paineis). Mesmo desenho: o DashboardRoute
          // cobre navegação, este handler cobre a revogação no meio da sessão.
          if (
            error.response.status === 403 &&
            (error.response.data as { error?: string } | undefined)?.error === 'dashboard_feature_not_enabled'
          ) {
            window.dispatchEvent(
              new CustomEvent('dashboard-feature-blocked', {
                detail: error.response.data,
              }),
            );
            return Promise.reject(error);
          }
          switch (error.response.status) {
            case 401: {
              // Não tenta refresh para a própria rota de refresh/login
              const url = originalRequest?.url || '';
              if (url.includes('/auth/refresh') || url.includes('/auth/login') || originalRequest?._retry) {
                break;
              }

              const refreshToken = localStorage.getItem('refresh_token');
              if (!refreshToken) {
                break;
              }

              if (this.isRefreshing) {
                // Outro request já está fazendo refresh — espera o novo token
                return new Promise((resolve) => {
                  this.subscribeTokenRefresh((token: string) => {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    resolve(this.api(originalRequest));
                  });
                });
              }

              originalRequest._retry = true;
              this.isRefreshing = true;

              try {
                const { data } = await axios.post(
                  `${config.api.baseURL}/auth/refresh/`,
                  { refresh_token: refreshToken }
                );
                const newToken = data.access_token;
                localStorage.setItem('access_token', newToken);

                this.onTokenRefreshed(newToken);
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return this.api(originalRequest);
              } catch {
                // Refresh falhou — limpa tokens e redireciona para login
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');
                this.refreshSubscribers = [];
                window.location.href = '/login';
                return Promise.reject(error);
              } finally {
                this.isRefreshing = false;
              }
            }
            case 403:
              console.error('Acesso proibido');
              break;
            case 404:
              console.error('Recurso não encontrado');
              break;
            case 500:
              console.error('Erro no servidor');
              break;
            default:
              console.error('Erro na API:', error.response.data);
          }
        } else if (error.request) {
          console.error('Sem resposta do servidor');
        } else {
          console.error('Erro:', error.message);
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Obtém token CSRF dos cookies (Django)
   */
  private getCsrfToken(): string | null {
    const name = 'csrftoken';
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [cookieName, cookieValue] = cookie.trim().split('=');
      if (cookieName === name) {
        return cookieValue;
      }
    }
    return null;
  }

  /**
   * Requisição GET
   */
  public async get<T = unknown>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.api.get<T>(url, config);
  }

  /**
   * Requisição POST
   */
  public async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.api.post<T>(url, data, config);
  }

  /**
   * Requisição PUT
   */
  public async put<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.api.put<T>(url, data, config);
  }

  /**
   * Requisição PATCH
   */
  public async patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.api.patch<T>(url, data, config);
  }

  /**
   * Requisição DELETE
   */
  public async delete<T = unknown>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.api.delete<T>(url, config);
  }

  /**
   * Upload de arquivo
   */
  public async uploadFile<T = unknown>(
    url: string,
    file: File,
    onUploadProgress?: (progressEvent: unknown) => void,
    timeoutMs?: number
  ): Promise<AxiosResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    return this.api.post<T>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
      // Uploads grandes (PDF/OCR) precisam de mais que o timeout padrão de 30s:
      // o tempo é transferência do arquivo + extração síncrona no backend.
      ...(timeoutMs ? { timeout: timeoutMs } : {}),
    });
  }
}

// Cria instância singleton
const apiService = new ApiService();

export default apiService;
